import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core";
import { KDialog } from "@kyburz/kdesign-system";
import dot from "dot-object";
import useSetState from "../../hooks/useSetState";
import { humanToSnake } from "../../utils/utils";
import Uploading from "./Uploading";
import { setAlert } from "../../redux/actions/actions";
import CSVImporter from "./CSVImporter";
import CSVMapper from "./CSVMapper";

const useStyles = makeStyles(() => ({
  root: {
    maxHeight: 400,
    height: 400,
  },
  csvWrapper: {
    margin: "auto",
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    width: 300,
    height: 50,
  },
}));

interface Props {
  open: boolean;
  toggleBulkUpload: () => void;
  elements: any;
  toRemove?: string[];
  importFunction: (req: any) => Promise<void>;
  body?: any;
}

const BulkUpload: React.FC<Props> = ({
  open,
  toggleBulkUpload,
  elements,
  toRemove = [],
  importFunction,
  body,
}: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [
    { csv, columnArray, uploading, uploadResult, request, delimiter },
    setState,
  ] = useSetState({
    delimiter: ";",
    csv: [],
    columnArray: [],
    uploading: false,
    uploadResult: {
      result: "importing",
      imported: null,
      updated: null,
      failed: null,
      errors: null,
    },
    request: [],
  });

  const csvColumns = csv[0]?.data;

  const dropdownElements: { label: string; value: string }[] = elements.map(
    (x) => ({
      label: humanToSnake(x),
      value: humanToSnake(x),
    }),
  );

  const handleChange = (value, name, column) => {
    if (name) {
      if (
        !columnArray.some((x) => x.column === name.split(".").pop()) &&
        value
      ) {
        setState({
          columnArray: [
            ...new Set([...columnArray, { column, mappedValue: value?.value }]),
          ],
        });
      } else {
        const array = columnArray.filter(
          (x) => x.column !== column.split(".").pop(),
        );
        setState({
          columnArray: array,
        });
      }
    }
  };

  const handleDelimiter = (value: string): void => {
    setState({ delimiter: value });
  };

  const getIndexInColumns = (parameter) => {
    const parsedParameter = parameter.split(".").pop();
    const columns = csvColumns?.map((x) => x.split(".").pop());
    const findParameter = columnArray.find(
      (x) => x.mappedValue === parsedParameter,
    );
    const column = findParameter?.column;
    return parsedParameter === "images"
      ? columns.indexOf(column) >= 0
        ? columns.indexOf(column)
        : []
      : columns.indexOf(column) >= 0
      ? columns.indexOf(column)
      : null;
  };

  const upload = () => {
    setState({ uploading: true });
    importFunction(request).then((res: any) => {
      if (res.data) {
        setState({ uploadResult: res.data });
      } else {
        setState({ uploading: false });
        const errorMessage =
          (res?.e?.response?.data?.errors &&
            JSON.stringify(res?.e?.response?.data?.errors[0])) ||
          (res?.e?.response?.data?.details &&
            JSON.stringify(res?.e?.response?.data?.details)) ||
          "An Error occured";
        dispatch(setAlert(errorMessage, "error"));
      }
    });
  };

  const autoAssignColumns = () => {
    const columns = csvColumns?.map((x) => x.split(".").pop());
    const existing = dropdownElements.map((y) => y.label);
    const merged = columns
      .filter((element) => existing.includes(element))
      .map((x) => ({ column: x, mappedValue: x }));
    setState({ columnArray: merged });
  };

  const handleCancel = () => {
    toggleBulkUpload();
    setState({
      csv: [],
      columnArray: [],
      uploading: false,
      uploadResult: {
        result: "importing",
        imported: null,
        updated: null,
        failed: null,
        errors: null,
      },
      delimiter: ";",
    });
  };

  const handleOnDrop = (data) => {
    const parsedData = data?.filter((x) => JSON.stringify(x.data) !== '[""]');
    setState({ csv: parsedData });
  };

  const handlePrimaryClick = () => {
    if (csv.length && uploadResult.result === "importing") {
      upload();
    }
    if (uploadResult.result !== "importing") {
      handleCancel();
    }
  };

  useEffect(() => {
    if (csv.length) {
      autoAssignColumns();
    }
  }, [csv]);

  useEffect(() => {
    const parsedBody = (x) =>
      body.reduce(
        (o, key) =>
          Object.assign(o, {
            [key]:
              key === "images" && x.data[getIndexInColumns(key)]
                ? [x.data[getIndexInColumns(key)]]
                : x.data[getIndexInColumns(key)],
          }),
        {},
      );
    const req = csv?.slice(1).map((x) => parsedBody(x));
    const parsedReq = req.map((obj) => dot.object(obj));
    setState({ request: parsedReq });
  }, [columnArray]);

  return (
    <KDialog
      open={open}
      title="Import CSV"
      size="sm"
      primaryCTAText={uploadResult.result === "importing" ? "Upload" : "Close"}
      primaryCTAonClick={csv.length ? handlePrimaryClick : null}
      secondaryCTAonClick={uploading ? null : handleCancel}
      secondaryCTAText="Cancel"
      disabled={uploading && uploadResult.result === "importing"}
    >
      <div className={classes.root}>
        {uploading ? (
          <Uploading uploadResult={uploadResult} />
        ) : csv.length && !uploading ? (
          <CSVMapper
            csv={csv}
            columnArray={columnArray}
            toRemove={toRemove}
            dropdownElements={dropdownElements}
            handleSelectChange={handleChange}
          />
        ) : (
          <CSVImporter
            handleOnDrop={handleOnDrop}
            delimiter={delimiter}
            handleDelimiter={handleDelimiter}
          />
        )}
      </div>
    </KDialog>
  );
};

export default BulkUpload;
